import React from 'react';
import styled from 'styled-components';
import ReportImpactSummaryHeader from './ReportImpactSummaryHeader';
import ReportPercentage from '../ReportPercentage';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactStar from
  'shared/design-system/components/TrueImpactStar';
import SummaryIcon from
  'npo/pages/ReportBuilder/Components/ImpactSummary/SummaryIcon';
import {
  IndicatorSectionKey,
  stageIdToString,
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import { impactSummaryTableGridStyles } from './ReportImpactSummaryHeader';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { capitalizeOnlyFirstLetter } from 'shared/utils/stringUtils';

interface LegacyImpactModelItem {
  stageId: number;
  forecast: string;
  successCriteria: string;
  sourcesAndAssumptions: string;
  dataQuality: number;
  measurement: string;
  measurementCategory: string;
  variance: string | null | undefined;
}

interface ReportImpactSummaryTableForLegacyProps {
  impactModel: LegacyImpactModelItem[];
}

const StyledDiv = styled.div`
  background-color: ${COLORS.whiteBase};
`;

const StyledSectionDiv = styled.div`
  border-top: 1px solid ${COLORS.grayLight};
  border-bottom: 1px solid ${COLORS.grayLight};
  .impact-summary-row {
    ${impactSummaryTableGridStyles}
    padding: ${SPACERS.medium} 16px;
    .report-success-criteria {
      margin-top: ${SPACERS.small};
      margin-bottom: ${SPACERS.small};
    }
    .report-star-wrapper {
      display: flex;
      gap: ${SPACERS.extraTiny};
      margin-bottom: ${SPACERS.tiny};
    }
    .report-estimation {
      margin-bottom: ${SPACERS.tiny};
    }
  }
`;

// Map stageId to IndicatorSectionKey
const stageIdToSectionKey = (stageId: number): IndicatorSectionKey => {
  switch (stageId) {
    case 1:
      return 'learn';
    case 2:
      return 'act';
    case 3:
      return 'succeed';
    case 4:
      return 'catalyzed';
    case 5:
      return 'reach';
    case 6:
      return 'capacityDevelopment';
    default:
      return 'reach';
  }
};

// Get star count based on measurement category
const getStarCount = (measurementCategory: string): number => {
  const category = measurementCategory.toLowerCase();
  if (category.includes('direct')) {
    return 5;
  } else if (category.includes('estimate') || category.includes('data')) {
    return 3;
  } else {
    return 1;
  }
};

// Parse variance string to number for display
const parseVariance = (variance: string | null | undefined): number | undefined => {
  if (variance === 'N/A' || !variance || typeof variance !== 'string') {
    return undefined;
  }
  const numericValue = parseFloat(variance.replace('%', ''));
  if (isNaN(numericValue)) {
    return undefined;
  }
  // Cap at 100% since it's a percentage
  return Math.min(numericValue, 100);
};

const ReportImpactSummaryTableForLegacy: React.FC<
  ReportImpactSummaryTableForLegacyProps
> = ({ impactModel }) => {
  if (!impactModel || impactModel.length === 0) return null;

  // Group items by stageId to handle multiple items per stage
  const groupedByStage = impactModel.reduce((acc, item) => {
    const stageKey = item.stageId;
    if (!acc[stageKey]) {
      acc[stageKey] = [];
    }
    acc[stageKey].push(item);
    return acc;
  }, {} as Record<number, LegacyImpactModelItem[]>);

  // Sort stages in the order: 6, 5, 1, 2, 3, 4
  const orderedStageIds = [6, 5, 1, 2, 3, 4];
  const stagesToRender = orderedStageIds.filter(
    stageId => groupedByStage[stageId],
  );

  return (
    <StyledDiv>
      <ReportImpactSummaryHeader />
      {stagesToRender.map(stageId => {
        const items = groupedByStage[stageId];
        const sectionKey = stageIdToSectionKey(stageId);

        return (
          <StyledSectionDiv key={stageId}>
            {items.map((item, index) => {
              const starCount = getStarCount(item.measurementCategory);
              const varianceValue = parseVariance(item.variance);

              return (
                <div
                  className="impact-summary-row"
                  key={`${stageId}-${index}`}
                >
                  <div>
                    {index === 0 && (
                      <SummaryIcon
                        size={40}
                        maxWidth={94}
                        sectionKey={sectionKey}
                        text={stageIdToString(sectionKey)}
                      />
                    )}
                  </div>
                  <div>
                    <TrueImpactText variant="body-small-bold">
                      {item.forecast}
                    </TrueImpactText>
                    <TrueImpactText
                      variant="body-small"
                      className="report-success-criteria"
                    >
                      <strong>Success criteria:</strong> {item.successCriteria}
                    </TrueImpactText>
                    <TrueImpactText variant="body-small">
                      <strong>Sources and assumptions:</strong>{' '}
                      {item.sourcesAndAssumptions || 'Not provided'}
                    </TrueImpactText>
                  </div>
                  <div>
                    <div className="report-star-wrapper">
                      <TrueImpactStar count={starCount} />
                    </div>
                    <TrueImpactText
                      variant="body-small-bold"
                      className="report-estimation"
                    >
                      {capitalizeOnlyFirstLetter(item.measurementCategory)}
                    </TrueImpactText>
                    <TrueImpactText variant="body-small">
                      {item.measurement}
                    </TrueImpactText>
                  </div>
                  <div>
                    <ReportPercentage percentage={varianceValue} />
                  </div>
                </div>
              );
            })}
          </StyledSectionDiv>
        );
      })}
    </StyledDiv>
  );
};

export default ReportImpactSummaryTableForLegacy;
